import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  ChatBlock,
  ChatContext,
  ChatConversation,
  ChatEditorPatch,
  ChatMessage,
  ChatStreamEvent,
  ChatToolTier,
} from '@klingerator/shared';
import { chatService } from '@/services/chatService';
import { extractErrorMessage } from '@/services/api';
import { toastService } from '@/services/ToastService';
import { readSseStream } from '@/utils/sse';
import { useProductEditorStore } from '@/stores/productEditor';
import { useSyncStore } from '@/stores/sync';

export interface UiThinkingBlock {
  type: 'thinking';
  text: string;
  running: boolean;
  startTs?: number;
  durationMs?: number;
}
export interface UiTextBlock {
  type: 'text';
  text: string;
}
/** Bevestigingskaart van een write/delete-tool (alleen tijdens de stream). */
export interface UiConfirmation {
  summary: string;
  details: string | null;
  status: 'pending' | 'submitting' | 'confirmed' | 'cancelled' | 'expired';
}
export interface UiToolBlock {
  type: 'tool_call';
  id: string;
  name: string;
  input: unknown;
  tier: ChatToolTier;
  status: 'preparing' | 'running' | 'awaiting' | 'done' | 'error';
  output: string | null;
  confirmation: UiConfirmation | null;
}
export type UiBlock = UiThinkingBlock | UiTextBlock | UiToolBlock;

export interface UiMessage {
  id: number | null;
  role: 'user' | 'assistant';
  content: string;
  blocks: UiBlock[];
  streaming?: boolean;
  error?: string | null;
  created_at: string;
}

const CONVERSATION_KEY = 'kl.chatConversation';

/** Opgeslagen bouwstenen (server) → UI-blokken; tool_result wordt bij zijn tool_use gevoegd. */
export function blocksToUi(blocks: ChatBlock[] | null | undefined): UiBlock[] {
  const ui: UiBlock[] = [];
  const byId = new Map<string, UiToolBlock>();
  for (const b of blocks ?? []) {
    if (b.type === 'thinking') ui.push({ type: 'thinking', text: b.text, running: false });
    else if (b.type === 'text') ui.push({ type: 'text', text: b.text });
    else if (b.type === 'tool_use') {
      const tc: UiToolBlock = {
        type: 'tool_call',
        id: b.id,
        name: b.name,
        input: b.input ?? null,
        tier: b.tier ?? 'read',
        status: 'done',
        output: null,
        confirmation: null,
      };
      ui.push(tc);
      byId.set(b.id, tc);
    } else if (b.type === 'tool_result') {
      const tc = byId.get(b.tool_use_id);
      if (tc) {
        tc.status = b.is_error ? 'error' : 'done';
        tc.output = b.output;
      }
    }
  }
  return ui;
}

export function messageToUi(m: ChatMessage): UiMessage {
  return {
    id: m.id,
    role: m.role,
    content: m.content,
    blocks: blocksToUi(m.blocks),
    created_at: m.created_at,
  };
}

/** Concept-wijzigingen van de assistent in de geopende editor zetten (de gebruiker slaat zelf op). */
function applyEditorPatch(patch: ChatEditorPatch) {
  const editor = useProductEditorStore();
  if (editor.productId !== patch.product_id) {
    toastService.warning(
      `De assistent heeft een voorstel voor product #${patch.product_id}, maar dat staat niet (meer) open. Open het product en vraag het opnieuw.`,
    );
    return;
  }
  const r = editor.applyChatPatch(patch);
  if (r.applied.length)
    toastService.info(
      `Assistent: ${r.applied.join(', ')} als concept in het formulier gezet — controleer en sla op`,
    );
  if (r.skipped.length) toastService.warning(`Assistent: niet toegepast — ${r.skipped.join(', ')}`);
}

/**
 * Een product is server-side gewijzigd door de assistent: dezelfde verversing als na de knoppen op de
 * productpagina (editor, lijst, boom, validatie, badges) via de sync-store; alleen een dirty editor
 * blijft staan, met een melding.
 */
async function onProductChanged(productId: number, action: string) {
  const outcome = await useSyncStore().externalChange(productId, action);
  if (outcome === 'dirty')
    toastService.warning(
      'De assistent heeft dit product gewijzigd, maar je hebt niet-opgeslagen wijzigingen. Sla op of verwerp ze en herlaad om de nieuwe versie te zien.',
    );
}

/** Chat-assistent: gesprekken, berichten en de streaming-afhandeling (SSE via fetch). */
export const useChatStore = defineStore('chat', () => {
  const conversations = ref<ChatConversation[]>([]);
  const currentId = ref<number | null>(null);
  const messages = ref<UiMessage[]>([]);
  const loading = ref(false);
  const streaming = ref(false);
  const error = ref<string | null>(null);
  const initialized = ref(false);
  /** Concepttekst in het invoerveld (blijft staan als het paneel dichtgaat) */
  const draft = ref('');
  /**
   * Aanbevelingen (suggest_actions) waarop al "Uitvoeren" is geklikt, per tool-aanroep-id → indexen.
   * In de store i.p.v. in het component: het bericht wordt na de stream opnieuw opgebouwd (nieuwe key).
   */
  const sentActions = ref<Record<string, number[]>>({});
  function markActionSent(toolUseId: string, index: number) {
    const cur = sentActions.value[toolUseId] ?? [];
    if (!cur.includes(index)) sentActions.value = { ...sentActions.value, [toolUseId]: [...cur, index] };
  }
  const isActionSent = (toolUseId: string, index: number) =>
    (sentActions.value[toolUseId] ?? []).includes(index);
  let controller: AbortController | null = null;

  const current = computed(() => conversations.value.find((c) => c.id === currentId.value) ?? null);

  function rememberConversation(id: number | null) {
    try {
      if (id) localStorage.setItem(CONVERSATION_KEY, String(id));
      else localStorage.removeItem(CONVERSATION_KEY);
    } catch {
      /* geen opslag beschikbaar */
    }
  }

  async function init() {
    if (initialized.value) return;
    initialized.value = true;
    await loadConversations();
    let remembered: number | null = null;
    try {
      remembered = Number(localStorage.getItem(CONVERSATION_KEY)) || null;
    } catch {
      remembered = null;
    }
    if (remembered && conversations.value.some((c) => c.id === remembered)) await select(remembered);
  }

  async function loadConversations() {
    try {
      conversations.value = await chatService.listConversations();
    } catch (e) {
      error.value = extractErrorMessage(e, 'Gesprekken laden mislukt');
    }
  }

  async function select(id: number) {
    if (streaming.value) stop();
    currentId.value = id;
    rememberConversation(id);
    loading.value = true;
    error.value = null;
    try {
      messages.value = (await chatService.listMessages(id)).map(messageToUi);
    } catch (e) {
      error.value = extractErrorMessage(e, 'Gesprek laden mislukt');
      messages.value = [];
    } finally {
      loading.value = false;
    }
  }

  function startNew() {
    if (streaming.value) stop();
    currentId.value = null;
    rememberConversation(null);
    messages.value = [];
    error.value = null;
  }

  async function remove(id: number) {
    await chatService.remove(id);
    conversations.value = conversations.value.filter((c) => c.id !== id);
    if (currentId.value === id) startNew();
  }

  async function rename(id: number, title: string) {
    const updated = await chatService.rename(id, title);
    conversations.value = conversations.value.map((c) => (c.id === id ? updated : c));
  }

  function upsertConversation(conv: ChatConversation) {
    const idx = conversations.value.findIndex((c) => c.id === conv.id);
    if (idx === -1) conversations.value.unshift(conv);
    else conversations.value.splice(idx, 1, conv);
    conversations.value.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
  }

  async function send(text: string, context: ChatContext | null = null) {
    const message = text.trim();
    if (!message || streaming.value) return;
    error.value = null;
    // Meteen "bezig" (en een AbortController) zodat dubbel versturen en stoppen ook tijdens het
    // aanmaken van het gesprek goed gaan.
    streaming.value = true;
    controller = new AbortController();
    const signal = controller.signal;
    if (!currentId.value) {
      try {
        const conv = await chatService.createConversation();
        upsertConversation(conv);
        currentId.value = conv.id;
        rememberConversation(conv.id);
      } catch (e) {
        error.value = extractErrorMessage(e, 'Gesprek starten mislukt');
        streaming.value = false;
        controller = null;
        return;
      }
    }
    if (signal.aborted) {
      streaming.value = false;
      controller = null;
      return;
    }
    const conversationId = currentId.value!;
    messages.value.push({
      id: null,
      role: 'user',
      content: message,
      blocks: [],
      created_at: new Date().toISOString(),
    });
    messages.value.push({
      id: null,
      role: 'assistant',
      content: '',
      blocks: [],
      streaming: true,
      created_at: new Date().toISOString(),
    });
    // Muteren via de reactieve proxy uit de store (anders geen reactiviteit).
    const msg = messages.value[messages.value.length - 1];
    draft.value = '';

    let openBlock: UiBlock | null = null;
    const push = <T extends UiBlock>(b: T): T => {
      msg.blocks.push(b);
      return msg.blocks[msg.blocks.length - 1] as T;
    };
    const findTool = (id: string | undefined) =>
      id
        ? (msg.blocks.find((b) => b.type === 'tool_call' && b.id === id) as UiToolBlock | undefined)
        : undefined;

    const handle = (ev: ChatStreamEvent) => {
      switch (ev.type) {
        case 'block_start':
          if (ev.blockType === 'thinking')
            openBlock = push({ type: 'thinking', text: '', running: true, startTs: Date.now() });
          else if (ev.blockType === 'text') openBlock = push({ type: 'text', text: '' });
          else {
            if (!findTool(ev.toolUseId))
              push({
                type: 'tool_call',
                id: ev.toolUseId || `tmp-${Date.now()}`,
                name: ev.toolName ?? '?',
                input: null,
                tier: 'read',
                status: 'preparing',
                output: null,
                confirmation: null,
              });
            openBlock = null;
          }
          break;
        case 'thinking_delta':
          if (openBlock?.type === 'thinking') openBlock.text += ev.text;
          break;
        case 'text_delta':
          if (openBlock?.type === 'text') openBlock.text += ev.text;
          else openBlock = push({ type: 'text', text: ev.text });
          msg.content += ev.text;
          break;
        case 'block_stop':
          if (openBlock?.type === 'thinking') {
            openBlock.running = false;
            openBlock.durationMs = Date.now() - (openBlock.startTs ?? Date.now());
          }
          openBlock = null;
          break;
        case 'tool_call': {
          const tc = findTool(ev.id);
          if (tc) {
            tc.status = 'running';
            tc.input = ev.input;
            tc.name = ev.name;
            tc.tier = ev.tier ?? tc.tier;
          } else
            push({
              type: 'tool_call',
              id: ev.id,
              name: ev.name,
              input: ev.input,
              tier: ev.tier ?? 'read',
              status: 'running',
              output: null,
              confirmation: null,
            });
          break;
        }
        case 'tool_result': {
          const tc = findTool(ev.id);
          if (tc) {
            tc.status = ev.isError ? 'error' : 'done';
            tc.output = ev.output;
          }
          break;
        }
        case 'confirmation_required': {
          // Write/delete-tool wacht op de gebruiker: kaart met knoppen bij de tool-chip; de stream loopt door.
          let tc = findTool(ev.id);
          if (!tc)
            tc = push({
              type: 'tool_call',
              id: ev.id,
              name: ev.name,
              input: ev.input,
              tier: ev.tier,
              status: 'awaiting',
              output: null,
              confirmation: null,
            });
          tc.tier = ev.tier;
          tc.input = ev.input;
          tc.status = 'awaiting';
          tc.confirmation = { summary: ev.summary, details: ev.details, status: 'pending' };
          break;
        }
        case 'confirmation_result': {
          const tc = findTool(ev.id);
          if (tc?.confirmation) tc.confirmation.status = ev.decision;
          if (tc && tc.status === 'awaiting') tc.status = ev.decision === 'confirmed' ? 'running' : 'done';
          break;
        }
        case 'editor_patch':
          applyEditorPatch(ev.patch);
          break;
        case 'product_changed':
          void onProductChanged(ev.product_id, ev.action);
          break;
        case 'warning':
          toastService.warning(ev.message);
          break;
        case 'error':
          msg.error = ev.message;
          break;
        case 'done':
          msg.id = ev.message.id;
          msg.content = ev.message.content;
          msg.blocks = blocksToUi(ev.message.blocks);
          // de server zet een mislukte beurt zelf als ⚠️-tekst in het bericht → niet dubbel tonen
          if (msg.error && ev.message.content.includes(msg.error)) msg.error = null;
          upsertConversation(ev.conversation);
          break;
        case 'turn_start':
          break;
      }
    };

    try {
      const res = await chatService.openStream(conversationId, { message, context }, signal);
      await readSseStream(res.body!, (frame) => {
        if (frame.event === 'end' || !frame.data || frame.data === '{}') return;
        try {
          handle(JSON.parse(frame.data) as ChatStreamEvent);
        } catch (e) {
          console.error('Chat: SSE-frame niet te lezen', e, frame.data);
        }
      });
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') {
        msg.content += (msg.content ? '\n\n' : '') + '⏹️ Antwoord onderbroken.';
        msg.blocks.push({ type: 'text', text: '⏹️ Antwoord onderbroken.' });
      } else {
        const m = extractErrorMessage(e, 'De verbinding met de assistent is verbroken');
        msg.error = m;
        error.value = m;
      }
    } finally {
      msg.streaming = false;
      for (const b of msg.blocks) if (b.type === 'thinking') b.running = false;
      for (const b of msg.blocks)
        if (
          b.type === 'tool_call' &&
          (b.status === 'running' || b.status === 'preparing' || b.status === 'awaiting')
        )
          b.status = 'error';
      streaming.value = false;
      controller = null;
    }
  }

  function stop() {
    controller?.abort();
  }

  /** Bevestig of annuleer een openstaande actie; de tool-uitvoer streamt verder over de open verbinding. */
  async function confirmTool(toolUseId: string, decision: 'confirm' | 'cancel'): Promise<void> {
    const conversationId = currentId.value;
    if (!conversationId) return;
    const block = messages.value
      .flatMap((m) => m.blocks)
      .find((b): b is UiToolBlock => b.type === 'tool_call' && b.id === toolUseId);
    const conf = block?.confirmation;
    if (!conf || conf.status !== 'pending') return;
    conf.status = 'submitting';
    try {
      await chatService.confirm(conversationId, toolUseId, decision);
      conf.status = decision === 'confirm' ? 'confirmed' : 'cancelled';
    } catch (e) {
      conf.status = 'pending'; // opnieuw proberen toestaan
      toastService.error(extractErrorMessage(e, 'Bevestigen mislukt'));
    }
  }

  return {
    sentActions,
    markActionSent,
    isActionSent,
    conversations,
    currentId,
    current,
    messages,
    loading,
    streaming,
    error,
    initialized,
    draft,
    init,
    loadConversations,
    select,
    startNew,
    remove,
    rename,
    send,
    stop,
    confirmTool,
  };
});
